import { AfterViewInit, Component, ElementRef, EventEmitter, Input, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { LayerService } from '../../../../services/layer.service';
import { HeatmapMode, isFeatureLayer, isGraphicsLayer, isHeatmapRenderer, isMapImageLayer, isSimpleRenderer, isUniqueValueRenderer, ISymbolChangeDetail, MapCoreLayer } from '../../../../types';
import { BaseComponent } from '../../../base.component';
import { ITab } from '../../../utilityComponents/tabs/tabs.component';
import { BaseSymbolEditorComponent } from '../baseSymbolEditor';
import { LayerOpacityEditorComponent } from '../layer-opacity-editor/layer-opacity-editor.component';
import { LineSymbolEditorComponent } from '../line-symbol-editor/line-symbol-editor.component';
import { PolygonSymbolEditorComponent } from '../polygon-symbol-editor/polygon-symbol-editor.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-symbol-editor-container-ng',
    templateUrl: './symbol-editor-container.component.html',
    styleUrls: ['../../../sharedStyles/base.scss', './symbol-editor-container.component.scss']
})
export class SymbolEditorContainerComponent extends BaseComponent implements AfterViewInit {
    esriLayer: MapCoreLayer;
    layerType: string;
    lineSymbol: __esri.SimpleLineSymbol;
    parentWindow: any;
    pointsAndLines: boolean = false;
    pointSymbol: __esri.SimpleMarkerSymbol | __esri.PictureMarkerSymbol;
    polygonSymbol: __esri.SimpleFillSymbol;
    textSymbol: __esri.TextSymbol;
    slotComponents: any[] = [];
    firstSymbol: SupportedSymbol;
    _symbols: __esri.Symbol[];
    heatmapRenderer: __esri.HeatmapRenderer = undefined;
    symbolColor: string;
    symbolEditorTabs: ITab[] = [
        { name: 'Points', active: false, isVisible: false },
        { name: 'Lines', active: false, isVisible: false },
        { name: 'Polygons', active: false, isVisible: false },
        { name: 'Text', active: false, isVisible: false },
        { name: 'Heatmap', active: false, isVisible: false }
    ];
    isLoaded: boolean = false;

    @Input() applyColorToEntireLayer: boolean = false;

    @Input() opacity: number;

    @Input() set symbols(value: __esri.Symbol[]) {
        this._symbols = value;
        this.getSymbols();
    }

    get symbols() {
        return this._symbols;
    }

    private _uniqueLayerId: string;
    // tslint:disable:cyclomatic-complexity
    @Input() set uniqueLayerId(val: string) {
        this._uniqueLayerId = val;
        this.esriLayer = null;
        this.pointSymbol = null;
        this.lineSymbol = null;
        this.polygonSymbol = null;
        this.textSymbol = null;
        this.heatmapRenderer = null;
        this.symbols = null;

        if (val) {
            this.esriLayer = this.layerService.getEsriLayerByUniqueId(this.uniqueLayerId);
            this.layerType = this.esriLayer.type;

            if (isMapImageLayer(this.esriLayer)) {
                this.symbols = this.esriLayer.sublayers.toArray().filter(x => x.renderer && (<__esri.SimpleRenderer>x.renderer).symbol).map(x => (<__esri.SimpleRenderer>x.renderer).symbol);
            } else if (isFeatureLayer(this.esriLayer)) {
                if (isSimpleRenderer(this.esriLayer.renderer)) {
                    this.symbols = [this.esriLayer.renderer.symbol];
                    if (this.esriLayer.mapcoreProps.heatmapMode === HeatmapMode.Auto && !this.layerService.checkIfHeatmapIsOnOrOff(this.esriLayer)) {
                        this.heatmapRenderer = this.esriLayer.mapcoreProps.heatmapRenderer;
                    }
                } else if (isUniqueValueRenderer(this.esriLayer.renderer) && this.esriLayer.renderer.uniqueValueInfos.some(i => i.symbol.type === 'picture-marker' || i.symbol.type === 'simple-line')) {
                    this.symbols = this.esriLayer.renderer.uniqueValueInfos.map(i => i.symbol);
                } else if (isHeatmapRenderer(this.esriLayer.renderer)) {
                    this.heatmapRenderer = this.esriLayer.renderer.clone();
                    if (isSimpleRenderer(this.esriLayer.mapcoreProps.renderer) && this.esriLayer.mapcoreProps.renderer.symbol) {
                        this.symbols = [this.esriLayer.mapcoreProps.renderer.symbol];
                    }
                }
            } else if (isGraphicsLayer(this.esriLayer)) {
                this.symbols = this.esriLayer.graphics.toArray().map(x => x.symbol);
            }

            this.isLoaded = true;
        } else {
            for (const slotComponent of this.slotComponents) {
                if (slotComponent.cancel) {
                    slotComponent.cancel();
                }
            }
            this.slotComponents = [];
        }

        if (this.esriLayer) {
            this.pointsAndLines = this.lineSymbol && this.pointSymbol ? true : false;
            if (this.esriLayer.mapcoreProps.applyColorToEntireLayer === undefined && (this.pointSymbol || this.lineSymbol) && this.firstSymbol.color && !(this.heatmapRenderer && isHeatmapRenderer(this.heatmapRenderer))) {
                this.esriLayer.mapcoreProps.applyColorToEntireLayer = false;
                this.esriLayer.mapcoreProps.color = this.firstSymbol.color.toCss(true);
            }
        }

        this.getSymbols();
    }
    get uniqueLayerId(): string {
        return this._uniqueLayerId;
    }
    // tslint:enable:cyclomatic-complexity

    @Input() updateLegendSymbol = new EventEmitter<boolean>();

    get symbolTypes() {
        return this.symbols.map(x => x.type);
    }

    private pointSymbolEditorRef: ElementRef<HTMLSlotElement>;
    @ViewChild('pointSymbolEditor') set pointSymbolEditor(pointSymbolEditor: ElementRef<HTMLSlotElement>) {
        if (pointSymbolEditor) {
            this.pointSymbolEditorRef = pointSymbolEditor;
            if (this.pointSymbol) {
                try {
                    const slotComponent = this.assignLayerSymbolToSlotComponent(this.pointSymbolEditorRef, 'mc-point-symbol-editor', this.pointSymbol);

                    if (this.applyColorToEntireLayer && this.symbolColor) {
                        slotComponent.symbolColor = this.symbolColor;
                    }
                } catch { }
            }
        }
    }

    private lineSymbolEditorRef: ElementRef<HTMLSlotElement>;
    @ViewChild('lineSymbolEditor') set lineSymbolEditor(lineSymbolEditor: ElementRef<HTMLSlotElement>) {
        if (lineSymbolEditor) {
            this.lineSymbolEditorRef = lineSymbolEditor;
            if (this.lineSymbol) {
                try {
                    const slotComponent = this.assignLayerSymbolToSlotComponent(this.lineSymbolEditorRef, 'mc-line-symbol-editor', this.lineSymbol) as LineSymbolEditorComponent;
                    slotComponent.layerType = this.layerType;

                    if (this.applyColorToEntireLayer && this.symbolColor) {
                        slotComponent.symbolColor = this.symbolColor;
                    }
                }
                catch { }
            }
        }
    }

    private polygonSymbolEditorRef: ElementRef<HTMLSlotElement>;
    @ViewChild('polygonSymbolEditor') set polygonSymbolEditor(polygonSymbolEditor: ElementRef<HTMLSlotElement>) {
        if (polygonSymbolEditor) {
            this.polygonSymbolEditorRef = polygonSymbolEditor;
            if (this.polygonSymbol) {
                try {
                    const slotComponent = this.assignLayerSymbolToSlotComponent(this.polygonSymbolEditorRef, 'mc-polygon-symbol-editor', this.polygonSymbol) as PolygonSymbolEditorComponent;
                    slotComponent.layerType = this.layerType;

                    if (this.applyColorToEntireLayer && this.symbolColor) {
                        slotComponent.symbolColor = this.symbolColor;
                    }
                }
                catch { }
            }
        }
    }

    private textSymbolEditorRef: ElementRef<HTMLSlotElement>;
    @ViewChild('textSymbolEditor') set textSymbolEditor(textSymbolEditor: ElementRef<HTMLSlotElement>) {
        if (textSymbolEditor) {
            this.textSymbolEditorRef = textSymbolEditor;
            this.assignLayerSymbolToSlotComponent(this.textSymbolEditorRef, 'mc-text-symbol-editor', this.textSymbol);
        }
    }

    private heatmapEditorRef: ElementRef<HTMLSlotElement>;
    @ViewChild('heatmapEditor') set heatmapEditor(heatmapEditor: ElementRef<HTMLSlotElement>) {
        this.heatmapEditorRef = heatmapEditor;
        if (heatmapEditor) {
            this.assignHeatmapSymboltoSlotComponent(this.heatmapEditorRef, 'mc-heatmap-editor', this.heatmapRenderer);
        }
    }

    private layerOpacityEditorRef: ElementRef<HTMLSlotElement>;
    @ViewChild('opacityEditor') set layerOpacityEditor(layerOpacityEditor: ElementRef<HTMLSlotElement>) {
        if (layerOpacityEditor) {
            this.layerOpacityEditorRef = layerOpacityEditor;
            try {
                const slotComponent = this.assignLayerSymbolToSlotComponent(this.layerOpacityEditorRef, 'mc-layer-opacity-editor') as any as LayerOpacityEditorComponent;
                if (this.uniqueLayerId) {
                    slotComponent.uniqueLayerId = this.uniqueLayerId;
                } else {
                    slotComponent.opacity = this.opacity;
                }
            }
            catch { }
        }
    }

    constructor(_vcr: ViewContainerRef, private readonly layerService: LayerService) { super(_vcr); }

    ngAfterViewInit() {
        this.isReady();
        this.setParentWindow();
    }

    async setParentWindow() {
        // TODO: Remove window references
        this.getClosestAncestor('mc-window').then((window: any) => {
            if (window) {
                this.parentWindow = window;
                this.parentWindow.addEventListener('openChange', open => {
                    if (open.detail) {
                        this.symbolColor = null;
                        this.opacity = 100;
                    } else {
                        this.close();
                    }
                });
            }
        });
    }

    getSymbols() {
        if (this.symbols && this.symbols.length > 0) {
            this.firstSymbol = this.symbols[0] as SupportedSymbol;
            this.lineSymbol = this.getSymbolBasedOnType(['simple-line', 'polyline']) as __esri.SimpleLineSymbol;
            this.pointSymbol = this.getSymbolBasedOnType(['simple-marker', 'picture-marker']) as __esri.SimpleMarkerSymbol | __esri.PictureMarkerSymbol;
            this.polygonSymbol = this.getSymbolBasedOnType(['simple-fill']) as __esri.SimpleFillSymbol;
            this.textSymbol = this.getSymbolBasedOnType(['text']) as __esri.TextSymbol;

            const pointTab = this.symbolEditorTabs.find((tab) => tab.name === 'Points');
            pointTab.active = this.firstSymbol === this.pointSymbol && this.heatmapRenderer == null;
            pointTab.isVisible = this.pointSymbol != null && this.heatmapRenderer == null;

            const lineTab = this.symbolEditorTabs.find((tab) => tab.name === 'Lines');
            lineTab.active = this.firstSymbol === this.lineSymbol && this.heatmapRenderer == null;
            lineTab.isVisible = this.lineSymbol != null && this.heatmapRenderer == null;

            const polygonTab = this.symbolEditorTabs.find((tab) => tab.name === 'Polygons');
            polygonTab.active = this.firstSymbol === this.polygonSymbol && this.heatmapRenderer == null;
            polygonTab.isVisible = this.polygonSymbol != null && this.heatmapRenderer == null;

            const textTab = this.symbolEditorTabs.find((tab) => tab.name === 'Text');
            textTab.active = this.firstSymbol === this.textSymbol && this.heatmapRenderer == null;
            textTab.isVisible = this.textSymbol != null && this.heatmapRenderer == null;
        }

        const heatmapTab = this.symbolEditorTabs.find((tab) => tab.name === 'Heatmap');
        heatmapTab.active = this.heatmapRenderer != null;
        heatmapTab.isVisible = this.heatmapRenderer != null;
    }

    assignHeatmapSymboltoSlotComponent(slotRef: ElementRef<HTMLSlotElement>, tagName: string, renderer?: __esri.HeatmapRenderer) {
        const slotComponent = this.getSlotAssignedComponent(slotRef, (<any>tagName));

        if (renderer) {
            slotComponent.heatmapRenderer = renderer;
        }

        if (this.uniqueLayerId) {
            slotComponent.uniqueLayerId = this.uniqueLayerId;
        }

        if (this.esriLayer) {
            slotComponent.heatmapMode = this.esriLayer.mapcoreProps.heatmapMode;
        }

        if (this.slotComponents.indexOf(slotComponent) < 0) {
            this.slotComponents.push(slotComponent);
        }

        return slotComponent;
    }

    assignLayerSymbolToSlotComponent(slotRef: ElementRef<HTMLSlotElement>, tagName: string, symbol?: any) {
        const slotComponent = this.getSlotAssignedComponent(slotRef, (<any>tagName)) as BaseSymbolEditorComponent;
        slotComponent.applyColorToEntireLayer = this.applyColorToEntireLayer;
        (<any>slotComponent).addEventListener('colorChange', e => this.colorChange(e));
        (<any>slotComponent).addEventListener('applyColorToEntireLayerChange', e => this.applyColorToEntireLayerCheckboxChange(e));
        if (symbol) {
            slotComponent.layerSymbol = symbol;
        }
        if (this.uniqueLayerId) {
            slotComponent.uniqueLayerId = this.uniqueLayerId;
        }
        if (this.slotComponents.indexOf(slotComponent) < 0) {
            this.slotComponents.push(slotComponent);
        }
        return slotComponent;
    }

    getSymbolBasedOnType(types: string[]) {
        if (this.symbols) {
            const foundArray = this.symbols.filter(x => types.includes(x.type));
            if (foundArray.length === 1) {
                return foundArray[0];
            }

            if (foundArray.length > 1) {
                // When there are more than one symbols of the same type on the layer, apply Color to Entire Layer.
                const symbol = foundArray[0];
                if (symbol.color) {
                    this.esriLayer.mapcoreProps.applyColorToEntireLayer = true;
                    this.esriLayer.mapcoreProps.color = symbol.color.toCss(true);
                }
                return symbol;
            }
        }
        return null;
    }

    // tslint:disable:cyclomatic-complexity
    apply() {
        for (const item of this.slotComponents) {
            if (item.applyChange) {
                const isHeatmapMode = this.esriLayer && this.layerService.checkIfHeatmapIsOnOrOff(this.esriLayer);
                if (item.heatmapRenderer) {
                    const renderer = item.applyChange();
                    if (isFeatureLayer(this.esriLayer)) {
                        this.heatmapRenderer = renderer.clone();

                        if (isHeatmapMode) {
                            this.esriLayer.renderer = this.heatmapRenderer;
                        }

                        this.esriLayer.mapcoreProps.heatmapRenderer = this.esriLayer.mapcoreProps.heatmapMode === HeatmapMode.Off
                            ? null
                            : this.heatmapRenderer;
                    }
                } else {
                    const oldId: string = item.layerSymbol ? item.layerSymbol.id : null;
                    const newSymbol = item.applyChange();
                    if (this.esriLayer && oldId && newSymbol) {
                        if (isMapImageLayer(this.esriLayer)) {
                            if (isHeatmapMode) {
                                (<__esri.SimpleRenderer>this.esriLayer.mapcoreProps.renderer).symbol = newSymbol;
                            } else {
                                (<__esri.SimpleRenderer>this.esriLayer.sublayers.toArray().find(x => x.renderer && (<any>(<__esri.SimpleRenderer>x.renderer).symbol).id === oldId).renderer).symbol = newSymbol;
                            }
                        } else if (isFeatureLayer(this.esriLayer)) {
                            if (isHeatmapMode) {
                                (<__esri.SimpleRenderer>this.esriLayer.mapcoreProps.renderer).symbol = newSymbol;
                            } else if (isSimpleRenderer(this.esriLayer.renderer)) {
                                this.esriLayer.renderer.symbol = newSymbol;
                            } else if (isUniqueValueRenderer(this.esriLayer.renderer) && this.esriLayer.renderer.uniqueValueInfos.some(i => i.symbol.type === 'picture-marker')) {
                                this.esriLayer.renderer.uniqueValueInfos.find(x => (<any>x.symbol).id === oldId).symbol = newSymbol;
                            }
                        } else if (isGraphicsLayer(this.esriLayer)) {
                            this.esriLayer.graphics.toArray().find(x => (<any>x.symbol).id === oldId).symbol = newSymbol;
                            for (let i = 0; i < this.symbols.length; i++) {
                                if ((<any>this.symbols[i]).id === oldId) {
                                    this.symbols.splice(i, 1, newSymbol);
                                }
                            }
                        }
                    }
                }
            }
        }

        if ((this.lineSymbol || this.pointSymbol || this.polygonSymbol) && (this.esriLayer && this.esriLayer.mapcoreProps.applyColorToEntireLayer)) {
            this.symbols.forEach(sym => {
                sym.color.setColor(this.esriLayer.mapcoreProps.color);
                if ((<__esri.SimpleMarkerSymbol | __esri.SimpleFillSymbol>sym).outline) {
                    (<__esri.SimpleMarkerSymbol | __esri.SimpleFillSymbol>sym).outline.color.setColor(this.esriLayer.mapcoreProps.color);
                }
            });
        }

        this.updateLegendSymbol.emit(true);

        if (this.uniqueLayerId) {
            const layer = this.layerService.getEsriLayerByUniqueId(this.uniqueLayerId);
            if (layer) {
                if (isGraphicsLayer(layer)) {
                    const changedGraphics = layer.graphics.clone();
                    layer.removeAll();
                    layer.addMany(changedGraphics.toArray());
                } else if (layer.type !== 'group') {
                    layer.refresh();
                }
            }
        }

        const opacityEditor = this.getSlotAssignedComponent(this.layerOpacityEditorRef, 'mc-layer-opacity-editor') as LayerOpacityEditorComponent;

        window.dispatchEvent(new CustomEvent<string>('symbolChange', { detail: this.uniqueLayerId }));
        window.dispatchEvent(new CustomEvent<ISymbolChangeDetail>('newSymbols', { detail: {
            layerId: this.uniqueLayerId,
            symbols: this.slotComponents.filter(sc => sc.nodeName.toLowerCase() !== 'mc-layer-opacity-editor').map(sc => sc.layerSymbol),
            opacity: opacityEditor.opacity
        } }));

        this.close();
    }
    // tslint:enable:cyclomatic-complexity

    colorChange(e: CustomEvent<string>) {
        this.symbolColor = e.detail;

        if (this.applyColorToEntireLayer) {
            for (const slotComponent of this.slotComponents) {
                (<BaseSymbolEditorComponent>slotComponent).symbolColor = e.detail;
            }
        }

        if (this.esriLayer) {
            this.esriLayer.mapcoreProps.color = e.detail;
        }
    }

    applyColorToEntireLayerCheckboxChange(e: CustomEvent<boolean>) {
        this.applyColorToEntireLayer = e.detail;

        if (this.esriLayer) {
            this.esriLayer.mapcoreProps.applyColorToEntireLayer = this.applyColorToEntireLayer;

            if (!e.detail) {
                this.esriLayer.mapcoreProps.color = null;
            }
        }
    }

    cancel() {
        this.close();
    }

    async close() {
        this.uniqueLayerId = null;
        this.closeWindow();
    }

    closeWindow() {
        if (this.parentWindow && this.parentWindow.isOpened) {
            this.parentWindow.close();
        }
    }
}

type SupportedSymbol = __esri.SimpleLineSymbol | __esri.SimpleMarkerSymbol | __esri.PictureMarkerSymbol | __esri.SimpleFillSymbol | __esri.TextSymbol;
